import { PlannerTask, ScheduledOccurrence, DashboardDailyMetrics, OccurrenceStatus } from '../types';

/**
 * Clamps any numeric score strictly between 1 and 10 as an integer.
 */
export function clampScore(score: number | undefined | null, fallback = 5): number {
  if (score === undefined || score === null || isNaN(score)) return fallback;
  return Math.min(10, Math.max(1, Math.round(score)));
}

/**
 * Heuristic calculator for automatic default score (1 to 10) based on domain context.
 */
export function calculateDefaultScore(params: {
  category?: string;
  linkedEntityType?: string;
  durationMinutes?: number;
  difficulty?: string;
  title?: string;
}): number {
  const { category = '', linkedEntityType = '', durationMinutes = 15, difficulty = 'Beginner', title = '' } = params;
  const cat = (category || linkedEntityType || '').toLowerCase();
  const t = title.toLowerCase();

  let base = 5;

  // Domain Category Weighting
  if (cat.includes('rehab') || cat.includes('physio') || t.includes('کمر') || t.includes('اصلاحی') || t.includes('rehab')) {
    base = 8; // High therapeutic priority
  } else if (cat.includes('fat-loss') || cat.includes('workout') || cat.includes('exercise') || t.includes('ورزش') || t.includes('چربی')) {
    base = 7;
  } else if (cat.includes('swedish') || cat.includes('sfi') || cat.includes('study') || cat.includes('learning') || t.includes('سوئدی') || t.includes('کلاس')) {
    base = 6;
  } else if (cat.includes('work') || t.includes('کار') || t.includes('اداره')) {
    base = 6;
  } else if (cat.includes('water') || cat.includes('hydration') || t.includes('آب')) {
    base = 3;
  } else if (cat.includes('routine') || cat.includes('habit')) {
    base = 5;
  }

  // Duration modifier
  if (durationMinutes >= 45) {
    base += 2;
  } else if (durationMinutes >= 30) {
    base += 1;
  } else if (durationMinutes <= 5) {
    base -= 1;
  }

  // Difficulty modifier
  if (difficulty === 'Advanced' || difficulty === 'Hard') {
    base += 1;
  } else if (difficulty === 'Beginner') {
    // Keep baseline balanced
  }

  return clampScore(base, 5);
}

export function getTaskDurationMinutes(startTime?: string | null, endTime?: string | null, category?: string, title?: string): number {
  if (startTime && endTime) {
    try {
      const [sh, sm] = startTime.split(':').map(Number);
      const [eh, em] = endTime.split(':').map(Number);
      let diff = (eh * 60 + em) - (sh * 60 + sm);
      if (diff < 0) diff += 24 * 60;
      if (diff > 0) return diff;
    } catch {
      // Fall through to category default
    }
  }

  // Sensible domain defaults when end_time is not explicitly specified
  const cat = (category || title || '').toLowerCase();
  if (cat.includes('work') || cat.includes('کار')) return 465; // ~7h 45m
  if (cat.includes('sfi') || cat.includes('class')) return 210; // 3.5h
  if (cat.includes('rehab') || cat.includes('physio')) return 20;
  if (cat.includes('fat-loss') || cat.includes('workout')) return 30;
  if (cat.includes('swedish')) return 25;
  return 30;
}

/**
 * Returns interval [startMinutes, endMinutes] from midnight for an occurrence.
 */
export function getOccurrenceInterval(occ: Partial<ScheduledOccurrence>): { start: number; end: number } {
  const startStr = occ.startTimeSnapshot || '08:00';
  const endStr = occ.endTimeSnapshot || occ.end_time_snapshot || null;

  let startMinutes = 8 * 60;
  try {
    const [sh, sm] = startStr.split(':').map(Number);
    startMinutes = (sh || 0) * 60 + (sm || 0);
  } catch {
    startMinutes = 480;
  }

  let endMinutes = startMinutes + 30;
  if (endStr) {
    try {
      const [eh, em] = endStr.split(':').map(Number);
      let calcEnd = (eh || 0) * 60 + (em || 0);
      if (calcEnd < startMinutes) calcEnd += 24 * 60;
      if (calcEnd > startMinutes) {
        endMinutes = calcEnd;
      } else {
        endMinutes = startMinutes + 30;
      }
    } catch {
      const dur = getTaskDurationMinutes(startStr, endStr, occ.categorySnapshot, occ.titleSnapshot);
      endMinutes = startMinutes + dur;
    }
  } else {
    const dur = getTaskDurationMinutes(startStr, null, occ.categorySnapshot, occ.titleSnapshot);
    endMinutes = startMinutes + dur;
  }

  return { start: startMinutes, end: endMinutes };
}

/**
 * Computes the union of intervals in minutes to avoid double-counting overlapping tasks.
 * e.g., [10:00-11:30] and [11:00-12:00] -> [10:00-12:00] = 120 minutes (not 150).
 */
export function calculateIntervalUnionMinutes(intervals: Array<{ start: number; end: number }>): number {
  const valid = intervals.filter(i => i.end > i.start);
  if (valid.length === 0) return 0;

  const sorted = valid.slice().sort((a, b) => a.start - b.start || a.end - b.end);
  const merged: Array<{ start: number; end: number }> = [];
  let cur = { ...sorted[0] };

  for (let i = 1; i < sorted.length; i++) {
    const next = sorted[i];
    if (next.start <= cur.end) {
      cur.end = Math.max(cur.end, next.end);
    } else {
      merged.push(cur);
      cur = { ...next };
    }
  }
  merged.push(cur);

  return merged.reduce((sum, item) => sum + Math.max(0, item.end - item.start), 0);
}

/**
 * Formats duration in minutes to user-friendly string based on language.
 */
export function formatMinutesDuration(minutes: number, lang = 'fa'): string {
  if (!minutes || minutes <= 0) return lang === 'fa' ? '۰ دقیقه' : '0 min';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;

  if (lang === 'fa') {
    if (h === 0) return `${m} دقیقه`;
    if (m === 0) return `${h} ساعت`;
    return `${h} ساعت و ${m} دقیقه`;
  }
  if (lang === 'sv') {
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h} tim`;
    return `${h} tim ${m} min`;
  }
  // English
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/**
 * Calculates exact time weights for required occurrences, using largest-remainder reconciliation
 * so that displayed individual percentages sum to exactly 100%.
 */
export function calculateOccurrenceTimeWeights(
  occurrences: ScheduledOccurrence[],
  totalScheduledMinutes: number
): Map<string, { durationMinutes: number; weightPercent: number }> {
  const map = new Map<string, { durationMinutes: number; weightPercent: number }>();
  const required = occurrences.filter(o => o.status !== 'cancelled' && o.status !== 'skipped' && Boolean(o.isRequiredSnapshot));
  
  if (required.length === 0 || totalScheduledMinutes <= 0) {
    occurrences.forEach(o => {
      const dur = getTaskDurationMinutes(o.startTimeSnapshot, o.endTimeSnapshot, o.categorySnapshot, o.titleSnapshot);
      map.set(o.id, { durationMinutes: dur, weightPercent: 0 });
    });
    return map;
  }

  const items = required.map(o => {
    const dur = getTaskDurationMinutes(o.startTimeSnapshot, o.endTimeSnapshot, o.categorySnapshot, o.titleSnapshot);
    const rawPct = (dur / totalScheduledMinutes) * 100;
    const floorPct = Math.floor(rawPct);
    const remainder = rawPct - floorPct;
    return { id: o.id, dur, rawPct, floorPct, remainder };
  });

  const currentSum = items.reduce((s, item) => s + item.floorPct, 0);
  let diff = Math.max(0, 100 - currentSum);

  const sorted = items.slice().sort((a, b) => b.remainder - a.remainder);
  const extraAllocated = new Set<string>();
  for (let i = 0; i < diff && i < sorted.length; i++) {
    extraAllocated.add(sorted[i].id);
  }

  items.forEach(item => {
    const finalPct = item.floorPct + (extraAllocated.has(item.id) ? 1 : 0);
    map.set(item.id, { durationMinutes: item.dur, weightPercent: finalPct });
  });

  occurrences.forEach(o => {
    if (!map.has(o.id)) {
      const dur = getTaskDurationMinutes(o.startTimeSnapshot, o.endTimeSnapshot, o.categorySnapshot, o.titleSnapshot);
      map.set(o.id, { durationMinutes: dur, weightPercent: 0 });
    }
  });

  return map;
}

/**
 * Gets effective score for a task (prioritizing custom score if scoreMode is custom).
 */
export function getEffectiveTaskScore(task: Partial<PlannerTask & { time?: string; startTime?: string; endTime?: string }>): number {
  if (task.scoreMode === 'custom' && typeof task.customScore === 'number' && task.customScore >= 1) {
    return clampScore(task.customScore, 5);
  }
  if (typeof task.defaultScore === 'number' && task.defaultScore >= 1) {
    return clampScore(task.defaultScore, 5);
  }
  const sTime = task.startTime || task.time || undefined;
  const dur = getTaskDurationMinutes(sTime, task.endTime, task.category, task.title);
  return calculateDefaultScore({
    category: task.category,
    linkedEntityType: task.linkedEntityType,
    title: task.title,
    durationMinutes: dur
  });
}

/**
 * Get 0-indexed Day of Week (0 = Sunday, 1 = Monday ... 6 = Saturday)
 * using local calendar parsing without UTC drift.
 */
export function getDayOfWeekFromDateStr(dateStr: string): number {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.getDay();
}

/**
 * Determines whether a PlannerTask is scheduled to occur on a given date (YYYY-MM-DD).
 */
export function isTaskDueOnDate(task: PlannerTask, dateStr: string): boolean {
  if (!task.isActive) return false;

  if (task.recurrenceType === 'daily') {
    return true;
  }

  if (task.recurrenceType === 'weekly') {
    const dow = getDayOfWeekFromDateStr(dateStr);
    const days = Array.isArray(task.daysOfWeek) ? task.daysOfWeek : [];
    return days.includes(dow);
  }

  if (task.recurrenceType === 'interval' && task.intervalDays && task.intervalDays > 0) {
    if (!task.createdAt) return true;
    const createdDate = task.createdAt.split('T')[0];
    const [cy, cm, cd] = createdDate.split('-').map(Number);
    const [ty, tm, td] = dateStr.split('-').map(Number);
    const createdMs = new Date(cy, cm - 1, cd).getTime();
    const targetMs = new Date(ty, tm - 1, td).getTime();
    const diffDays = Math.floor((targetMs - createdMs) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays % task.intervalDays === 0;
  }

  return false;
}

/**
 * Canonical Dashboard Metric Calculation Engine (Dashboard Policy v2.3)
 * Pure function: calculates time-weighted daily performance (Completed Scheduled Minutes / Total Scheduled Minutes)
 * with interval union deduplication, while maintaining task counts and task scores as supplementary metrics.
 */
export function calculateDailyMetrics(
  occurrences: ScheduledOccurrence[],
  targetDateStr?: string,
  todayDateStr?: string
): DashboardDailyMetrics {
  // Exclude cancelled occurrences from all metric calculations
  const validOccurrences = occurrences.filter(o => o.status !== 'cancelled');
  
  // Skipped tasks are excluded from denominator so they do NOT penalize performance
  const activeOccurrences = validOccurrences.filter(o => o.status !== 'skipped');
  const requiredOccurrences = activeOccurrences.filter(o => Boolean(o.isRequiredSnapshot));
  const optionalOccurrences = activeOccurrences.filter(o => !o.isRequiredSnapshot);

  const completedRequired = requiredOccurrences.filter(o => o.status === 'completed');
  const completedOptional = optionalOccurrences.filter(o => o.status === 'completed');

  // Time-Weighted Interval Calculations (Dashboard Policy v2.3)
  const requiredIntervals = requiredOccurrences.map(getOccurrenceInterval);
  const completedRequiredIntervals = completedRequired.map(getOccurrenceInterval);
  const completedOptionalIntervals = completedOptional.map(getOccurrenceInterval);

  const totalScheduledMinutes = calculateIntervalUnionMinutes(requiredIntervals);
  const completedScheduledMinutes = calculateIntervalUnionMinutes(completedRequiredIntervals);
  const bonusMinutes = calculateIntervalUnionMinutes(completedOptionalIntervals);

  // Secondary Task Score Points
  const availablePoints = requiredOccurrences.reduce((sum, o) => sum + (o.effectiveScoreSnapshot || 5), 0);
  const earnedPoints = completedRequired.reduce((sum, o) => sum + (o.effectiveScoreSnapshot || 5), 0);
  const bonusPoints = completedOptional.reduce((sum, o) => sum + (o.effectiveScoreSnapshot || 5), 0);

  // Task Counts
  const totalTasks = requiredOccurrences.length;
  const completedTasks = completedRequired.length + completedOptional.length;
  const remainingTasks = requiredOccurrences.filter(o => o.status === 'pending' || o.status === 'missed').length;

  let completionRate = 0;
  if (totalTasks > 0) {
    completionRate = Math.round((completedRequired.length / totalTasks) * 100);
  } else if (completedOptional.length > 0) {
    completionRate = Math.round((completedOptional.length / optionalOccurrences.length) * 100);
  }

  // Determine day type & score status
  let dayType: 'standard' | 'rest_day' | 'upcoming' = 'standard';
  let scoreStatus: 'calculated' | 'N/A' | 'upcoming' = 'calculated';
  let dailyPerformance: number | null = null;

  const isFuture = Boolean(targetDateStr && todayDateStr && targetDateStr > todayDateStr);

  if (validOccurrences.length === 0 || (totalTasks === 0 && optionalOccurrences.length === 0)) {
    // Scheduled Rest Day
    dayType = isFuture ? 'upcoming' : 'rest_day';
    scoreStatus = 'N/A';
    dailyPerformance = null;
    completionRate = 0;
  } else if (totalTasks === 0 && totalScheduledMinutes === 0) {
    // Only optional tasks on this day
    dayType = 'standard';
    scoreStatus = 'N/A';
    dailyPerformance = null;
  } else if (isFuture && completedTasks === 0) {
    // Future date with scheduled tasks
    dayType = 'upcoming';
    scoreStatus = 'upcoming';
    dailyPerformance = null;
  } else {
    // Standard day with scheduled required tasks
    dayType = 'standard';
    scoreStatus = 'calculated';
    // Core Formula: (Completed Scheduled Minutes / Total Scheduled Minutes) * 100
    if (totalScheduledMinutes > 0) {
      dailyPerformance = Math.min(100, Math.round((completedScheduledMinutes / totalScheduledMinutes) * 100));
    } else if (totalTasks > 0) {
      dailyPerformance = Math.min(100, completionRate);
    } else {
      dailyPerformance = null;
    }
  }

  return {
    totalScheduledMinutes,
    completedScheduledMinutes,
    dailyPerformance,
    totalTasks,
    completedTasks,
    remainingTasks,
    completionRate: Math.min(100, Math.max(0, completionRate)),
    availablePoints,
    earnedPoints,
    bonusPoints,
    dailyScore: dailyPerformance, // Aligned with primary performance
    scoreStatus,
    dayType,
    bonusMinutes,
    occurrences: validOccurrences
  };
}
